import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { expenseService } from '../../services/expenseService';
import InfoIcon from '../../components/InfoIcon';
import { expenseHints } from '../../utils/formHints';

const EditExpensePage = () => {
  const { expenseId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'other',
    amount: '',
    expenseDate: '',
    vendor: '',
    paymentMethod: 'cash',
    status: 'pending',
    isTaxDeductible: true,
    isReimbursable: false,
    hasReceipt: false,
    receiptUrl: '',
    documentUrls: [],
    projectId: '',
    tags: [],
    notes: '',
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [newTag, setNewTag] = useState('');

  const expenseCategories = [
    { value: 'vehicle_expenses', label: 'Vehicle Expenses' },
    { value: 'equipment_purchase', label: 'Equipment Purchase' },
    { value: 'equipment_maintenance', label: 'Equipment Maintenance' },
    { value: 'tools_supplies', label: 'Tools & Supplies' },
    { value: 'office_supplies', label: 'Office Supplies' },
    { value: 'software_subscriptions', label: 'Software Subscriptions' },
    { value: 'insurance', label: 'Insurance' },
    { value: 'licensing_permits', label: 'Licensing & Permits' },
    { value: 'professional_services', label: 'Professional Services' },
    { value: 'advertising_marketing', label: 'Advertising & Marketing' },
    { value: 'training_education', label: 'Training & Education' },
    { value: 'travel_meals', label: 'Travel & Meals' },
    { value: 'utilities', label: 'Utilities' },
    { value: 'rent_lease', label: 'Rent & Lease' },
    { value: 'subcontractor_payments', label: 'Subcontractor Payments' },
    { value: 'materials_inventory', label: 'Materials & Inventory' },
    { value: 'bank_fees', label: 'Bank Fees' },
    { value: 'interest_expense', label: 'Interest Expense' },
    { value: 'depreciation', label: 'Depreciation' },
    { value: 'other', label: 'Other' },
  ];

  const paymentMethods = [
    { value: 'cash', label: 'Cash' },
    { value: 'credit_card', label: 'Credit Card' },
    { value: 'debit_card', label: 'Debit Card' },
    { value: 'check', label: 'Check' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'other', label: 'Other' },
  ];

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'paid', label: 'Paid' },
    { value: 'reimbursed', label: 'Reimbursed' },
  ];

  useEffect(() => {
    fetchExpense();
  }, [expenseId, token]);

  const fetchExpense = async () => {
    if (!token || !expenseId) return;
    
    setIsLoading(true);
    try {
      const response = await expenseService.getExpenseById(expenseId, token);
      if (response.status === 'success') {
        const expense = response.data.expense;
        setFormData({
          title: expense.title || '',
          description: expense.description || '',
          category: expense.category || 'other',
          amount: expense.amount?.toString() || '',
          expenseDate: expense.expenseDate ? new Date(expense.expenseDate).toISOString().split('T')[0] : '',
          vendor: expense.vendor || '',
          paymentMethod: expense.paymentMethod || 'cash',
          status: expense.status || 'pending',
          isTaxDeductible: expense.isTaxDeductible ?? true,
          isReimbursable: expense.isReimbursable ?? false,
          hasReceipt: expense.hasReceipt ?? false,
          receiptUrl: expense.receiptUrl || '',
          documentUrls: expense.documentUrls || [],
          projectId: expense.projectId?.toString() || '',
          tags: expense.tags || [],
          notes: expense.notes || '',
        });
      } else {
        throw new Error(response.message || 'Failed to fetch expense');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch expense');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileUpload = async (e, type = 'receipt') => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingFile(true);
    try {
      // Mock file upload - replace with actual implementation
      const formData = new FormData();
      formData.append('file', file);
      
      // Simulate upload delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const mockUrl = `https://example.com/uploads/${file.name}`;
      
      if (type === 'receipt') {
        setFormData(prev => ({
          ...prev,
          receiptUrl: mockUrl,
          hasReceipt: true
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          documentUrls: [...prev.documentUrls, mockUrl]
        }));
      }
    } catch (err) {
      setError('Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleRemoveDocument = (index) => {
    setFormData(prev => ({
      ...prev,
      documentUrls: prev.documentUrls.filter((_, i) => i !== index)
    }));
  };

  const handleAddTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');

    try {
      // Validate required fields
      if (!formData.title.trim()) {
        throw new Error('Title is required');
      }
      if (!formData.amount || parseFloat(formData.amount) <= 0) {
        throw new Error('Amount must be greater than 0');
      }

      const updateData = {
        ...formData,
        amount: parseFloat(formData.amount),
        projectId: formData.projectId ? parseInt(formData.projectId) : null,
        documentUrls: formData.documentUrls.length > 0 ? formData.documentUrls : null,
        tags: formData.tags.length > 0 ? formData.tags : null,
      };

      const response = await expenseService.updateExpense(expenseId, updateData, token);
      
      if (response.status === 'success') {
        navigate(`/admin/expenses/${expenseId}`, { 
          state: { 
            message: 'Expense updated successfully!',
            type: 'success'
          }
        });
      } else {
        throw new Error(response.message || 'Failed to update expense');
      }
    } catch (err) {
      setError(err.message || 'Failed to update expense');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading expense details...</p>
        </div>
      </div>
    );
  }

  if (error && !formData.title) {
    return (
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
          <div className="mt-4">
            <button
              onClick={() => navigate('/admin/expenses')}
              className="text-blue-600 hover:text-blue-800"
            >
              ← Back to Expenses
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow-lg rounded-lg p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Edit Expense</h1>
            <p className="text-gray-600 mt-2">Update expense details and information</p>
          </div>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-10">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-500">Loading expense details...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 flex items-center">
                    Title *
                    <InfoIcon hint={expenseHints.title} />
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Brief description of the expense"
                  />
                </div>

                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700 flex items-center">
                    Category *
                    <InfoIcon hint={expenseHints.category} />
                  </label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {expenseCategories.map(category => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="amount" className="block text-sm font-medium text-gray-700 flex items-center">
                    Amount *
                    <InfoIcon hint={expenseHints.amount} />
                  </label>
                  <div className="mt-1 relative">
                    <span className="absolute left-3 top-2 text-gray-500">$</span>
                    <input
                      type="number"
                      id="amount"
                      name="amount"
                      value={formData.amount}
                      onChange={handleChange}
                      step="0.01"
                      min="0"
                      required
                      className="block w-full pl-8 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="expenseDate" className="block text-sm font-medium text-gray-700 flex items-center">
                    Expense Date *
                    <InfoIcon hint={expenseHints.expenseDate} />
                  </label>
                  <input
                    type="date"
                    id="expenseDate"
                    name="expenseDate"
                    value={formData.expenseDate}
                    onChange={handleChange}
                    required
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label htmlFor="vendor" className="block text-sm font-medium text-gray-700 flex items-center">
                    Vendor
                    <InfoIcon hint={expenseHints.vendor} />
                  </label>
                  <input
                    type="text"
                    id="vendor"
                    name="vendor"
                    value={formData.vendor}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Name of the seller or service provider"
                  />
                </div>

                <div>
                  <label htmlFor="paymentMethod" className="block text-sm font-medium text-gray-700 flex items-center">
                    Payment Method
                    <InfoIcon hint={expenseHints.paymentMethod} />
                  </label>
                  <select
                    id="paymentMethod"
                    name="paymentMethod"
                    value={formData.paymentMethod}
                    onChange={handleChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {paymentMethods.map(method => (
                      <option key={method.value} value={method.value}>
                        {method.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Status - only show for admin/manager */}
                {['admin', 'manager'].includes(user?.role) && (
                  <div>
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700 flex items-center">
                      Status
                      <InfoIcon hint={expenseHints.status} />
                    </label>
                    <select
                      id="status"
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {statusOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 flex items-center">
                  Description
                  <InfoIcon hint={expenseHints.description} />
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Additional details about this expense"
                />
              </div>

              {/* Flags */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isTaxDeductible"
                    name="isTaxDeductible"
                    checked={formData.isTaxDeductible}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isTaxDeductible" className="ml-2 block text-sm text-gray-700 flex items-center">
                    Tax Deductible
                    <InfoIcon hint={expenseHints.isTaxDeductible} />
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isReimbursable"
                    name="isReimbursable"
                    checked={formData.isReimbursable}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isReimbursable" className="ml-2 block text-sm text-gray-700 flex items-center">
                    Reimbursable
                    <InfoIcon hint={expenseHints.isReimbursable} />
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="hasReceipt"
                    name="hasReceipt"
                    checked={formData.hasReceipt}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="hasReceipt" className="ml-2 block text-sm text-gray-700 flex items-center">
                    Has Receipt
                    <InfoIcon hint={expenseHints.hasReceipt} />
                  </label>
                </div>
              </div>

              {/* Receipt Upload */}
              {formData.hasReceipt && (
                <div>
                  <label htmlFor="receiptUpload" className="block text-sm font-medium text-gray-700 flex items-center">
                    Receipt Upload
                    <InfoIcon hint={expenseHints.receiptUrl} />
                  </label>
                  <div className="mt-1 flex items-center">
                    <input
                      type="file"
                      id="receiptUpload"
                      name="receiptUpload"
                      onChange={(e) => handleFileUpload(e, 'receipt')}
                      className="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-md file:border-0
                        file:text-sm file:font-semibold
                        file:bg-blue-50 file:text-blue-700
                        hover:file:bg-blue-100"
                      accept="image/*,.pdf"
                      disabled={uploadingFile}
                    />
                    {uploadingFile && <span className="ml-3 text-sm text-gray-500">Uploading...</span>}
                  </div>
                  {formData.receiptUrl && (
                    <div className="mt-2">
                      <a href={formData.receiptUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
                        View Receipt
                      </a>
                    </div>
                  )}
                </div>
              )}

              {/* Notes */}
              <div>
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 flex items-center">
                  Notes
                  <InfoIcon hint={expenseHints.notes} />
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={2}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Any additional notes or details"
                />
              </div>

              {/* Tags */}
              <div>
                <label htmlFor="newTag" className="block text-sm font-medium text-gray-700 flex items-center">
                  Tags
                  <InfoIcon hint={expenseHints.tags} />
                </label>
                <div className="mt-1 flex">
                  <input
                    type="text"
                    id="newTag"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    className="block w-full border border-gray-300 rounded-l-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Add tag"
                  />
                  <button
                    type="button"
                    onClick={handleAddTag}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-r-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Add
                  </button>
                </div>
                {formData.tags.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {formData.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="ml-1.5 inline-flex text-blue-400 hover:text-blue-600 focus:outline-none"
                        >
                          <span className="sr-only">Remove tag</span>
                          &times;
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <div className="flex justify-end pt-4 border-t">
                <button
                  type="button"
                  onClick={() => navigate('/admin/expenses')}
                  className="mr-3 px-4 py-2 text-sm text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default EditExpensePage;
